import { Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { combineLatest, map, Observable } from 'rxjs'
import { ScopeVersion } from '@app/core/model/scope-version'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { CdnConfig } from '@app/core/model/cdn-config.model'
import { User } from '@app/core/model/user.model'
import { AuthService } from '@app/core/service/auth.service'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import { DataMappingService } from '@app/features/scoping/service/data-mapping.service'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { ScopeActions } from '@app/features/scoping/store/actions/scoping.actions'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { MappedEntity } from '@app/core/model/definitions/mapped-entity.interface'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { plainToInstance } from 'class-transformer'
import { ChangeRequestActions } from '../../store/actions/change-request.actions'
import { ChangeRequestSelectors } from '../../store/selectors/change-request.selectors'

@Component({
  selector: 'change-request-list',
  templateUrl: './change-request-list.component.html',
  styleUrls: ['./change-request-list.component.scss'],
})
export class ChangeRequestListComponent implements OnChanges {
  @Input() currentScope: ScopeVersion
  @Input() currentChangeRequest: ChangeRequest
  @Input() cdnConfig: CdnConfig
  @Input() sections: ScopeSection[]

  netChangesDeliverables$: Observable<Deliverable[]> = this.store$.select(
    ChangeRequestSelectors.selectNetChangesDeliverables
  )
  netChangesScopeVersion$: Observable<ScopeVersion>
  scopeFees$: Observable<MappedEntity<FeeItemInstance>[]>

  mainColumnClass: string
  componentMainColumnClass: string
  roleMainColumnClass: string

  get loggedInUser(): User {
    return this.authService.loggedInUser
  }

  constructor(
    private store$: Store,
    private authService: AuthService,
    private mappingService: DataMappingService,
    protected scopeElementsService: ScopeElementsService
  ) {
    this.netChangesScopeVersion$ = combineLatest([
      this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
      this.store$.select(ChangeRequestSelectors.selectNetChangesScope),
    ]).pipe(
      map(([currentScope, netChangesScope]) => {
        if (!currentScope || !netChangesScope?.calculatedValues) return null
        return plainToInstance(ScopeVersion, {
          ...currentScope,
          calculatedValues: netChangesScope.calculatedValues,
        })
      })
    )
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentScope?.previousValue?.identity?.id !== this.currentScope?.identity?.id && this.currentScope) {
      this.initializeTableData()
    }

    if (changes.currentChangeRequest && this.currentChangeRequest) {
      this.loadNetChanges()
    }
  }

  private loadNetChanges(): void {
    if (this.currentScope?.identity?.id && this.currentChangeRequest?.id) {
      this.store$.dispatch(
        ChangeRequestActions.getNetChanges({
          scopeId: this.currentScope.identity.id,
          changeRequestId: this.currentChangeRequest.id,
        })
      )
    }
  }

  private initializeTableData(): void {
    this.store$.dispatch(ScopeActions.getAllFeeItems())

    this.scopeElementsService.setUserColumns(this.currentScope)
    this.scopeElementsService.setFeeColumns()

    this.setColumnClasses()

    this.scopeFees$ = this.store$.pipe(
      select(ScopeOverviewSelectors.selectScopeFees),
      map((feeItems) =>
        this.mappingService.transformArray<FeeItemInstance>(feeItems, this.scopeElementsService.scopeFeeColumns)
      )
    )
  }

  // Sets CSS grid class names for the HTML layout based on the number of visible optional columns.
  // e.g. 0 optional columns visible → 'eleven--columns' (widest); 5 visible → 'six--columns' (narrower)
  private setColumnClasses(): void {
    this.mainColumnClass = this.scopeElementsService.calculateMainColumnClass()
    this.componentMainColumnClass = this.scopeElementsService.calculateComponentMainColumnClass()
    this.roleMainColumnClass = this.scopeElementsService.calculateRoleMainColumnClass()
  }
}
